/**
 * Общие типы контента и состояния. Один файл на клиент и сервер: то, что уезжает
 * по /api/content, и то, что лежит в сейве, должно описываться одними и теми же
 * словами — иначе рассинхрон обнаружится в рантайме, а не в typecheck.
 */

/**
 * Адрес узла: `<docId>#<nodeId>`, где docId — путь от `content/` без `.md`.
 * Пустой nodeId означает вступление файла — текст до первого `##`.
 */
export type NodeAddr = string;

import type { EntityKind, EntityMention } from './entities.ts';
import type { Cell, Grid, MinigameDoc, Thread } from './untangle.ts';

export type DocType = 'scene' | 'room' | 'item' | 'word' | 'person' | 'reference' | 'transition' | 'minigame';

/** Зарезервированные ключи атрибутов узла (07-оболочка-тз, «Узлы, атрибуты, переходы»). */
export interface Attrs {
  if: string | null;
  set: string[];
  unset: string[];
  give: string[];
  take: string[];
  cost: number | null;
  once: boolean;
  goto: NodeAddr | null;
  tag: string[];
  /** Чем опция называется в строке ввода, если `<глагол> <предмет>` читается плохо. */
  label: string | null;
  /**
   * Предметы и выходы, которые есть в этом состоянии комнаты. Складываются
   * с постоянными из frontmatter; вычитания нет — если предмет приходится
   * вычитать, значит он не постоянный и объявлен не там.
   */
  items: string[];
  exits: RoomExit[];
  /**
   * Именованные даты, которые ставит узел: `- dates: {blueCard: 31.12.2026}`.
   * Срок объявляется в `episode.yaml`, а узел его двигает — так работает не только
   * Blue Card, но любой срок, который однажды понадобится.
   */
  dates: Record<string, string>;
  /**
   * Номер страницы предмета. Секция с `page` — состояние вещи, а не действие:
   * её заголовок не становится глаголом и команды не создаёт. Порядок задаёт
   * число, состояние в сейве хранится по заголовку.
   */
  page: number | null;
  /**
   * Разрыв во времени перед узлом — подпись, а не строка прозы
   * (07-оболочка-тз, «Монтажный кадр»): `через час`, `под утро`.
   *
   * Отдельная сущность нужна затем, что «Через час тот же парень находит Тоби
   * у стойки» тратит на служебную склейку **первую строку кадра** — ту, которая
   * обязана показывать. Подпись выносит разрыв из прозы и предъявляет его
   * до того, как игрок начнёт читать.
   */
  timeLabel: string | null;
  /**
   * Физическое ожидание (07-оболочка-тз, «Физическое ожидание»): узел держит
   * свой безымянный маршрут, пока не пройдёт `ms` активного времени игры.
   *
   * Механика редкая и нужна там, где содержанием является само пережитое
   * время: экспертиза в отделении идёт девяносто секунд, и Марго с игроком
   * ждут вместе. От `expires` отличается направлением: окно ответа убирает
   * необязательную реплику, ожидание удерживает обязательный маршрут.
   */
  wait: { id: string; ms: number } | null;
  /**
   * Переход молчания в раунде «надавить» ([[07b-надавить-тз]]): миллисекунды
   * активного времени, после которых движок исполняет его сам.
   *
   * Это не `expires`. Окно ответа убирает одну необязательную реплику и ничего
   * не выбирает за игрока; `timeout` выбирает заранее написанную ветку, в
   * которой Марго промолчала, — и потому он виден в списке как обычная опция.
   */
  timeout: number | null;
  /**
   * Помета на переходе: эта возможность заработана прежними действиями или
   * знаниями Марго (07-оболочка-тз, «Индикация заработанных реплик и потери
   * предметов»). В списке команда получает значок `✦`, и ничего больше: ни
   * своего цвета, ни объяснения, чем именно открыта.
   *
   * Ставит автор. Наличие `if` само по себе наградой не делает: условие бывает
   * и обычной развилкой состояния — «Тоби ушёл», «свет уже выключен».
   */
  earned: boolean;
  /**
   * Помета на переходе: команда закрывает текущий набор возможностей и продолжает
   * историю. Не «важная команда» и не предупреждение о последствиях в сюжете —
   * она сообщает ровно то, что персонаж знает, а игрок из интерфейса не видит:
   * вернуться к оставшимся действиям комнаты или разговора будет нельзя.
   */
  advance: boolean;
}

export function emptyAttrs(): Attrs {
  return {
    if: null,
    set: [],
    unset: [],
    give: [],
    take: [],
    cost: null,
    once: false,
    goto: null,
    tag: [],
    label: null,
    items: [],
    exits: [],
    dates: {},
    page: null,
    timeLabel: null,
    wait: null,
    timeout: null,
    earned: false,
    advance: false,
  };
}

/**
 * Опция — единственная единица ввода (07-оболочка-тз, «Опция»).
 *
 * Для игрока у неё есть только `label`. Всё остальное — движок, и в интерфейсе
 * не проявляется никак: переход в сцене, глагол с целью и служебная команда
 * выглядят и исполняются одинаково.
 */
/**
 * Категория происхождения опции (07-оболочка-тз, «Опция»). Путь ввода, выбора,
 * исполнения и записи в историю у всех один — категория нужна только раскладке
 * и цвету.
 *
 * Назначается **по источнику опции, а не по её словам**: действие предмета
 * `позвонить` — тоже `environment`, а авторская реплика со словом «взять»
 * останется `story`. В Markdown не пишется никогда.
 */
export type OptionKind = 'environment' | 'story' | 'system';

export interface Option {
  label: string;
  kind: OptionKind;
  /** Куда ведёт. null — опция исполняет атрибуты и не меняет позицию. */
  target: NodeAddr | null;
  attrs: Attrs;
  /** Глагол, если опцию построил генератор; у меток переходов — null. */
  verb: string | null;
  /** Цель глагола: id предмета, комнаты или слова. */
  object: string | null;
  /**
   * Сдвигает ли опция игрока. Сцены и комнаты — места, туда переходят; предметы
   * и собеседники — ответы, их текст приходит в поток, а игрок остаётся, где был.
   */
  moves: boolean;
  /**
   * Семья: какой генератор комнаты создал эту команду (07-оболочка-тз,
   * «Сворачивание повторяющихся действий комнаты»). У авторской опции — `null`.
   *
   * Семья собирается по метаданным генератора, а не по первому слову: две
   * случайно похожие авторские команды семьёй не становятся, и `осмотреть
   * колонку` из `осмотреть: items` не склеивается с авторским `осмотреть
   * внимательнее`.
   */
  family: { key: string; phrase: string } | null;
}

/**
 * Генератор, который сервер раскрыть не может: `words` и `inventory` живут
 * в сейве, а не в контенте. Раскрывается клиентом при сборке каталога.
 */
export interface PendingOption {
  verb: string;
  from: 'words' | 'inventory';
}

/**
 * Генератор в исходном виде. Игроку не виден никогда — нужен валидатору: без него
 * глагол, на который ни один предмет не отвечает, не выдаёт ни одной опции и потому
 * проходит незамеченным, хотя объявлен нигде не был.
 */
export interface GeneratorRef {
  verb: string;
  phrase: string;
  source: string;
}

/**
 * Адрес помещения ([[13-навигация-и-комнаты-тз]], «Адреса переходов»).
 *
 * `persistent` обязателен, `stage` и `node` — нет: ссылка без среза значит
 * «то же помещение в текущем срезе», ссылка без узла — «вход в помещение».
 */
export interface RoomRef {
  persistent: string;
  stage?: string;
  node?: string;
}

/**
 * Выход из комнаты ([[13a-локальные-подписи-выходов-тз]]).
 *
 * Адрес и формулировка — разные сущности, и это главная причина отдельного
 * типа. Одна и та же комната называется по-разному в зависимости от точки
 * входа: со двора идут «в общагу», а не «в жилой коридор», из лифта — «на этаж
 * 2», а не «в кафедральный коридор». Текст команды принадлежит выходу,
 * идентичность помещения не меняется, и комнат-дубликатов ради надписи
 * заводить не надо.
 */
export interface RoomExit {
  /** Канон адреса: `rooms-virt/<persistent>[:<stage>][#node]`. */
  ref: string;
  /**
   * Локальное дополнение к фразе генератора: `в общагу`, `на этаж 2`. `null` —
   * форма комнаты назначения, как было всегда. Это буквальный текст, а не
   * ссылка, не команда с глаголом и не псевдоним: предлог и падеж пишет автор.
   */
  target: string | null;
}

export interface Node {
  id: string;
  addr: NodeAddr;
  /**
   * Файл, в котором узел написан. Отдельно от адреса, потому что адрес у него
   * теперь логический: собранная комната приходит из двух файлов, и ругаться
   * надо на тот, где текст лежит руками автора.
   */
  file: string;
  /** Откуда узел пришёл в собранное представление. Нужно `/adm` и ошибкам. */
  part: 'own' | 'common' | 'version';
  /** Внутриигровая дата сцены. Ею штампуются флаги, которые узел ставит. */
  date: string | null;
  /** Строка заголовка в файле — чтобы валидатор ругался с адресом, а не вообще. */
  line: number;
  attrs: Attrs;
  text: string;
  options: Option[];
  pending: PendingOption[];
  generators: GeneratorRef[];
}

export interface Doc {
  id: string;
  docId: string;
  /** Абсолютный путь: нужен только для сообщений об ошибках. */
  path: string;
  type: DocType;
  label: string;
  /**
   * Готовое дополнение к команде: `колонку`, `в аудиторию`. Оболочка не склоняет
   * русский язык — форма пишется в заметке, вместе с предлогом. По умолчанию
   * совпадает с `label`: `учебник`, `окно`, `Тоби` в падеже не меняются.
   */
  target: string;
  /** Формы для отдельных глаголов, если общей не хватает: `подойти: к доске`. */
  targets: Record<string, string>;
  date: string | null;
  fm: Record<string, unknown>;
  nodes: Node[];
  /** Комнаты: выходы и предметы. У выхода, кроме адреса, бывает своя подпись. */
  exits: RoomExit[];
  items: string[];
  /** Предметы: глаголы, работающие только когда предмет на руках. */
  inHand: string[];
  /**
   * Предметы: контейнер, в который вложена эта вещь (07-оболочка-тз, «Вложенные
   * предметы — контейнер и его окна»). Окно компьютера принадлежит компьютеру:
   * в комнате его нет, открывается оно только внутри контейнера, и `закрыть`
   * возвращает в то же самое место. `null` — обычная вещь.
   */
  parent: string | null;
  /**
   * Комнаты: именованная входная нода. Первый вход идёт в неё, а не в безымянное
   * вступление; `null` — входом остаётся вступление.
   */
  entry: string | null;
  /**
   * Комнаты: открыто ли помещение в этом срезе. `false` прячет сгенерированный
   * выход в него, но связь в графе остаётся — закрытая дверь не то же самое,
   * что отсутствующий файл.
   */
  available: boolean;
  /**
   * Сцены и комнаты: хранить ли поток после ухода (07-оболочка-тз, «Лог
   * контекста и повторный вход»). У собранной комнаты значение версии среза
   * перекрывает общее.
   */
  log: boolean;
  /**
   * Сворачиваются ли здесь семьи команд (07-оболочка-тз, «Сворачивание
   * повторяющихся действий комнаты»).
   *
   * Решение принимается **на место целиком** и на сборке, а не на каждую
   * семью и не на каждый ход: иначе список переставляется под игроком от
   * одного поднятого флага, а место с единственной семьёй сворачивается
   * в одну строку, из которой нечего выбирать. Считается по самому плотному
   * узлу места при полностью раскрытых группах и снятых условиях.
   */
  collapse: boolean;
  /**
   * Предметы: открывает ли осмотр свой экран (07-оболочка-тз, «Предмет,
   * у которого есть свой экран»). Ставится тегом `screen` на любом узле вещи.
   *
   * Контейнер и книга открывают уровень сами — окнами и страницами. Вещь
   * с одним ответом не открывает его ничем, и собственное действие такой вещи
   * показать негде: в инвентаре её нет, она стоит в комнате. Тег — ровно это
   * разрешение, и он же обязывает экран быть непустым (правило `screen`).
   */
  screen: boolean;
  /** Страницы предмета: id секций в порядке `page`. У остальных заметок пусто. */
  pages: string[];
  /**
   * Где объявлен блок `options`. Узлы после раскрытия все получают общий список
   * комнаты, поэтому посчитать объявления по ним уже нельзя — а знать, что автор
   * написал блок дважды, надо.
   *
   * С файлом, а не одной строкой: у собранной комнаты блоков законно два —
   * общий и версии, — и ругаться надо только на два блока в одном файле.
   */
  optionBlocks: { file: string; line: number }[];
}

export interface Palette {
  bg: string;
  fg: string;
  dim: string;
  accent: string;
  [key: string]: string;
}

export interface RendererDef {
  id: string;
  palette: Palette;
  /** Набор символов рамки — только для оверлеев: `light`, `heavy`, `double`. */
  frame: string;
  /** Символ линейки между потоком и вводом: `light`, `heavy`, `double`, `dashed`, `none`. */
  rule: string;
  /**
   * Шкала времени в раунде «надавить» ([[07b-надавить-тз]]): `blocks`, `dots`.
   * Знак и длина — свойство машины, как линейка и рамка.
   */
  timer: string;
  /** `rows` — сколько строк должно влезать в окно; ширина следует из неё (metrics.ts). */
  font: { family: string; size: number; rows: number; line: number };
  effects: string[];
  ambience: string | null;
  keyboard: string | null;
}

export interface EpisodeDef {
  id: string;
  title: string;
  renderer: string;
  entry: NodeAddr;
  /** Глаголы персонажа. Показываются в строке подсказок. */
  verbs: string[];
  /** Глаголы, которые приносят предметы. В строке подсказок не показываются никогда. */
  itemVerbs: string[];
  characters: string[];
  /**
   * Короткие экранные имена говорящих: `полицейский → полиц.`
   *
   * Метка в тексте остаётся человеческой — автор пишет `> полицейский —`,
   * и читает это он, а не игрок. На экран же имя идёт в свою колонку, и то,
   * что в неё не влезает, ломает абзац. Сокращение объявляется явно и один
   * раз: угадывать его оболочка не должна, а «полиц.» — авторское слово.
   */
  speakers: Record<string, string>;
  /** Переопределение палитры персонажа эпизодом: в прологе у Марго цветные волосы. */
  paletteOverride: Record<string, string>;
  ambience: string | null;
  /**
   * Сроки эпизода: имя → подпись в статусе и начальная дата. Дальше их двигают
   * узлы (`- dates: {blueCard: 31.12.2026}`), а спрашивать о них можно в `if`
   * через `date:blueCard`. Заведено не под Blue Card, а под любой срок.
   */
  dates: Record<string, { label: string; at: string | null; expired: string }>;
  /**
   * Обучающий слой: где висит подсказка-пример и что в ней написано. Слой
   * отдельный от терминала — это инструкция к игре, а не игра.
   */
  tutorial: { at: NodeAddr | null; hint: string };
  /**
   * Заметки, закрытые для показа: переходы в них не появляются в строке ввода.
   *
   * Это не механика игры, а рубильник для демо — поэтому он живёт в `episode.yaml`,
   * который не приезжает из Obsidian. Резать условиями в самих заметках нельзя:
   * следующая синхронизация вернёт их как были.
   */
  closed: string[];
}

/**
 * Портрет — пиксель-арт на полублоках, а не ASCII из букв (07-оболочка-тз).
 *
 * `grid` — сетка индексов: строка = ряд пикселей, символ = ключ палитры. Рендерер
 * парует строки и выдаёт `▀`: верхний пиксель становится цветом текста, нижний —
 * цветом фона. Отсюда панель в 24 клетки шириной и вдвое меньшей высоты.
 */
export interface Portrait {
  grid: string[];
  /** Ключ палитры → цвет, уже с учётом переопределения эпизодом. Нет ключа — фон. */
  colors: Record<string, string>;
  /** В пикселях; высота всегда чётная. */
  width: number;
  height: number;
}

export interface CharacterDef {
  id: string;
  label: string;
  /** Портреты по эпизодам: палитру переопределяет эпизод. */
  portraits: Record<string, Portrait>;
}

export interface WordDef {
  id: string;
  label: string;
  category: string;
  text: string;
  /** Части карточки, которые добавляются по мере появления флагов-источников. */
  details?: { text: string; if: string | null }[];
}

/**
 * Статья справочника (07-оболочка-тз, «Справочник»). Заметка `reference/<id>.md`,
 * а не строка в общем файле: у статьи есть название, категория и текст, который
 * автор правит там же, где пишет всё остальное.
 *
 * Справочник **глобальный**: термин, заведённый в прологе, нужен и в главе 2,
 * а раскладывать статьи по эпизодам значило бы либо дублировать их, либо решать,
 * чей он.
 */
export interface ReferenceDef {
  id: string;
  /** Название термина: `шкала ИНЕС`. Его же игрок печатает после `справочник`. */
  label: string;
  /** Только для сортировки на экране: `физика`, `места`, `порядок`, `быт`. */
  category: string;
  text: string;
}

/**
 * Карточка перехода ([[14-переходы-и-даты-тз]]) — единственное, что двигает
 * игровой календарь.
 *
 * До неё дату приносила заметка, в которой стоит игрок, и это ломалось об
 * очевидное: общая комната и старый предмет живут в нескольких днях сразу.
 * Теперь дату и срез устанавливает явный файл, а комнаты и сцены календаря
 * не несут вовсе.
 *
 * Тело перехода — ровно один безымянный маршрут. Ни прозы, ни атрибутов:
 * условия и выбор принадлежат сцене, откуда игрок пришёл; карточка показывает
 * уже сделанный выбор.
 */
export interface TransitionDef {
  id: string;
  docId: string;
  /**
   * Эпизод-владелец: переход лежит в его папке и может вести только в его
   * комнату или сцену. При подтверждении он же становится `activeEpisode`
   * ([[14-переходы-и-даты-тз]], «Новый файл»).
   */
  episode: string;
  /** Срез мира, который переход устанавливает. Строка: `00`, `03a`, `05`. */
  stage: string;
  /** Календарная дата, `ДД.ММ.ГГГГ`. С неё считается статус и штампы флагов. */
  date: string;
  /** Место для карточки: печатается как написано. */
  location: string;
  /** Подпись монтажной связи: `два дня спустя`. До четырёх слов. */
  timeLabel: string | null;
  /** Куда переход ведёт: собранное помещение или сцена. */
  target: NodeAddr;
}

/**
 * Мини-игра «Распутать мысль» ([[07a-мини-игра]]).
 *
 * Поле — одна заметка `type: minigame`: геометрия в YAML целыми клетками,
 * тексты обычным Markdown. Узел завершения живёт в том же файле и исполняется
 * только после победы, поэтому прямые ссылки на него запрещены.
 */
export interface MinigameDef {
  id: string;
  docId: string;
  label: string;
  /** Правило, которое исполняет движок. Единственный подтип первой версии. */
  subtype: string;
  grid: Grid;
  /** Стабилизирует соответствие нитей текстовым позициям. */
  seed: number;
  /** ID локального узла, который исполняется после победы и нового `Enter`. */
  complete: string;
  /**
   * Начальная и известная автору решённая координата каждой точки. `solution`
   * нужна валидатору и `/adm`: единственным ответом она не является, победу
   * не проверяет и в клиентскую сборку не уезжает.
   */
  points: Record<number, { start: Cell; solution?: Cell }>;
  threads: Thread[];
  documents: MinigameDoc[];
  /** Что не так с текстом документов — разбор нашёл, валидатор скажет. */
  problems: string[];
}

/** Срез мира эпизода: дата у всех его переходов одна. */
export interface StageDef {
  stage: string;
  date: string;
  /** Переходы, которые его устанавливают: их может быть несколько. */
  transitions: string[];
}

export interface GameContent {
  title: string;
  saveVersion: number;
  episodes: EpisodeDef[];
  renderers: Record<string, RendererDef>;
  characters: Record<string, CharacterDef>;
  words: Record<string, WordDef>;
  /**
   * Справочник: id статьи → статья. Живёт отдельно от «Дела» намеренно —
   * двадцать аббревиатур в списке улик, и список перестаёт читаться как
   * расследование.
   */
  reference: Record<string, ReferenceDef>;
  /** Карточки перехода по id: они устанавливают срез и дату. */
  transitions: Record<string, TransitionDef>;
  /**
   * Мини-игры по docId: от адреса узла до поля — один шаг, а в сейве они
   * лежат по `id`, как предметы.
   */
  minigames: Record<string, MinigameDef>;
  /**
   * Срезы мира по эпизоду, в порядке появления. Набор задаётся не автором
   * отдельным списком, а самими переходами: срез существует тогда, когда в него
   * есть чем войти.
   */
  stages: Record<string, StageDef[]>;
  /** Срезы, в которых достижима заметка; нужны графу и отладочным входам. */
  docStages: Record<string, string[]>;
  /** Все узлы игры по адресу — движку больше ничего не нужно. */
  nodes: Record<NodeAddr, Node>;
  docs: Record<string, Doc>;
}

/**
 * Флаг — не булево, а запись: он помнит внутриигровую дату сцены, в которой был
 * поставлен (07-оболочка-тз, «Флаг помнит, когда его поставили»). Реальное время
 * не используется нигде и никогда.
 */
export interface FlagState {
  value: boolean;
  at: string | null;
}

/** Сейв: состояние, а не сцена (07-оболочка-тз, «Сейв»). */
export interface StreamEntry {
  /**
   * `grant` — выданное слово: механика должна быть видна, иначе её как бы нет.
   * `lost` — вещь ушла из инвентаря: та же работа в обратную сторону, своим
   * цветом палитры (07-оболочка-тз, «Индикация заработанных реплик и потери
   * предметов»).
   * `time` — разрыв во времени перед кадром: подпись, а не строка прозы.
   * `visit` — граница нового посещения: запись без текста, которая отделяет
   * восстановленный лог от того, что игрок видит сейчас (07-оболочка-тз,
   * «Граница нового посещения»).
   */
  kind: 'text' | 'echo' | 'card' | 'grant' | 'lost' | 'time' | 'visit';
  /** Текст без разметки — ровно то, что увидит игрок. */
  text: string;
  /**
   * Размеченные сущности, доступные **в момент вывода** (07-оболочка-тз, «Явно
   * размеченные сущности»). Считаются один раз, здесь, а не при каждой отрисовке:
   * «слово подсвечено, если карточка уже есть» — про тот момент, когда текст
   * показали. Иначе абзац из начала игры загорался бы задним числом, стоит
   * игроку получить слово через два часа, и подсветка перестала бы значить
   * «это можно набрать прямо сейчас».
   */
  mentions?: EntityMention[];
  /**
   * Что выдала эта строка (`kind: 'grant'`). Нужно панели по `2`: слово, которое
   * Марго только что получила, в тексте не размечено — его никто не упоминал,
   * его **дали**. А игрок, нажимающий `2` сразу после выдачи, спрашивает именно
   * про него.
   */
  granted?: { kind: EntityKind | 'item'; id: string; label: string };
}

/**
 * Незаконченное физическое ожидание. В сейве, потому что перезагрузка не должна
 * ни обнулять его, ни завершать по настенным часам: считается только активное
 * время игры, и оно живёт вместе с прогрессом.
 */
export interface WaitState {
  /** Узел, который ждёт: ожидание принадлежит месту, а не игре вообще. */
  node: NodeAddr;
  id: string;
  ms: number;
  /** Сколько активного времени уже прожито. */
  elapsed: number;
}

export interface SaveState {
  saveVersion: number;
  /** id слова → степень знания. Серое знает игрок, но не Марго. */
  words: Record<string, 'grey' | 'white'>;
  flags: Record<string, FlagState>;
  inventory: string[];
  /**
   * Узлы, чей сплэш уже показан. Один человек — один сплэш за игру, поэтому
   * помнит об этом сейв, а не сессия. Считаем по узлам, а не по персонажу:
   * так цикл в диалоге не покажет лицо второй раз, а рамочные два сплэша
   * Марго (первый и последний экран) остаются двумя разными.
   */
  splashes: string[];
  chapter: string;
  /**
   * Что сейчас открыто внутри вещи: её id → id открытого.
   *
   * У многостраничного предмета это id секции — именно id, а не номер: `page`
   * задаёт только порядок, и автор вправе его переставить, не ломая сохранение.
   * У контейнера — id вложенного предмета: компьютер, к которому вернулись,
   * показывает то окно, которое на нём было открыто.
   */
  itemStates: Record<string, string>;
  /**
   * Сроки: имя → дата. Начальные приходят из `episode.yaml`, дальше их двигают узлы.
   * Текущей даты здесь нет и быть не должно — она приходит из `currentDate`,
   * установленной последним подтверждённым переходом, а не из заметки, в которой
   * стоит игрок.
   */
  dates: Record<string, string>;
  /** Входили ли уже в стартовый узел: иначе при перезагрузке он сыграет дважды. */
  started: boolean;
  /**
   * Игровой контекст, установленный последней подтверждённой карточкой перехода
   * ([[14-переходы-и-даты-тз]], «Дата и сохранение»).
   *
   * Отсюда берутся дата статуса, остаток срока Blue Card и штамп `flags[*].at`.
   * Ни заметка, в которой стоит игрок, ни системные часы к этой дате отношения
   * не имеют. `null` в полях значит «контекст ещё не установлен»: до первой
   * карточки в игре допустим только неигровой пролог интерфейса.
   */
  activeStage: string | null;
  currentDate: string | null;
  lastTransitionId: string | null;
  /**
   * Где игрок остановился в каждом помещении: ключ `roomStateKey`, значение —
   * id узла. Состояние принадлежит срезу, а не файлу: новый срез получает своё,
   * и Тоби, ушедший в субботу, не возвращается при следующем входе.
   */
  roomStates: Record<string, string>;
  /**
   * Где Марго находится: docId комнаты, в которую она вошла последней. Это
   * показывает статус (07-оболочка-тз, «Экран»).
   *
   * Разговор и открытый предмет его не меняют — они не места, а то, чем игрок
   * занят на этом месте. Подтверждённый переход сбрасывает: новый контекст
   * начинается либо с комнаты, которая себя и запишет, либо со сцены, у которой
   * своего места нет.
   */
  activeRoom: string | null;
  /**
   * Предмет, открытый уровнем: книга в чтении или контейнер с окнами
   * (07-оболочка-тз, «Вложенные предметы»). Пока он открыт, список состоит
   * из него одного, а комната ждёт снаружи.
   *
   * В сейве, а не в сессии: перезагрузка на открытой книге обязана вернуть
   * книгу, а не выкинуть игрока в комнату с непонятно откуда взятым текстом.
   */
  openItem: string | null;
  /**
   * Поля мини-игр по `id` ([[07a-мини-игра]], «Состояние и сейв»).
   *
   * Координаты после каждого принятого шага, `solved` после победы и
   * `completionApplied` после первого исполнения узла завершения. Выбранная
   * точка, подсветка, пересечения и переставленный текст — производные: они
   * считаются заново из координат и контента, иначе одна и та же геометрия
   * однажды дала бы два разных текста.
   */
  minigames: Record<string, { points: Record<number, Cell>; solved: boolean; completionApplied: boolean }>;
  /**
   * Приостановленные места (07-оболочка-тз, «Неявный `resume`»): адреса, на
   * которых стоял игрок, открывая сцену или поле мини-игры. Последний —
   * сверху.
   *
   * Возврат по такому адресу — не новый вход, а возобновление: поток остаётся
   * на экране, текст той же устойчивой ноды не повторяется, `reentry` не
   * исполняется и границы посещения не появляется. Разметки у `resume` нет:
   * это термин документации, а не конструкция контента.
   *
   * Стек, а не одна запись, потому что цепочка настоящая: из комнаты открыли
   * диспетчер учёбы, из него — поле, а вернуться поле обязано в комнату. С
   * одной записью комната в этой цепочке теряется, и закрытое поле выглядит
   * новым посещением.
   */
  resume: NodeAddr[];
  /**
   * Предъявленная игроку история по контекстам (07-оболочка-тз, «Лог контекста
   * и повторный вход»): комнаты под ключом `(episode, stage, persistent)`,
   * сцены — `(episode, stage, scene id)`.
   *
   * Буфер активного контекста лежит здесь независимо от `log: true` — иначе
   * перезагрузка открывала бы строку ввода над пустым потоком. При уходе из
   * контекста без `log: true` буфер удаляется, но **ключ остаётся**: он и значит
   * «здесь уже были», и по нему движок понимает, что вход повторный.
   *
   * Это история, а не состояние: восстановленные блоки не исполняют `give`,
   * `set`, `once` и автоматические маршруты, а опции, условия и текущий узел
   * считаются заново из обычного сейва.
   */
  logs: Record<string, StreamEntry[]>;
  /**
   * Незаконченное физическое ожидание — не более одного за раз. Хранится
   * прожитое активное время, а не момент начала: настенные часы в счёт не идут,
   * и закрытая на ночь вкладка ожидание не заканчивает.
   */
  wait: WaitState | null;
  /**
   * Незаконченный раунд «надавить» ([[07b-надавить-тз]], «Сейв и лог»): узел
   * и остаток активного времени. Хранится, чтобы перезагрузка не давала новую
   * полную попытку, а возвращение в раунд продолжало его с того же остатка.
   *
   * Вторым сюжетным состоянием это не является: что уже исполнено, помнят
   * обычные ноды, флаги и `once`.
   */
  pressure: { node: NodeAddr; remainingMs: number } | null;
  /**
   * Экран управления уже показан. Показывается он один раз, перед первой
   * комнатой, но переоткрывается командой: модалка, исчезнувшая навсегда, —
   * не обучение.
   */
  taught: boolean;
  /**
   * Подсказка-пример отработана: игрок ввёл ровно то, что она предлагала.
   * Уходит она по делу, а не по таймеру — таймер снял бы её у того, кто читает
   * медленно, и оставил бы висеть у того, кто уже всё понял.
   */
  hinted: boolean;
  episodeState: {
    episode: string;
    at: NodeAddr;
    /** Узлы с `once`, которые уже сыграли. */
    used: string[];
  };
}
